import React, { useState, useEffect } from 'react';
import {
  BUILTIN_REMINDERS, INTERVAL_LABELS,
  migrateAssignment,
  effectiveActiveCount,
  effectiveAssigned, isAutoEnrolled, isEligible,
  computeSaleAnnivRun,
  soldDateForContact,
} from './assignmentUtils';
import { scopedKey } from './scope';
import { confirmDiscard } from './confirmDiscard';
import { BulkAssignPanel, computeBulkAssign, computeBulkUnassign } from './BulkAssign';
import Icon from './Icon';
import './ReminderAssignments.css';

// The built-in reminder defs (assignmentUtils) still carry emoji `icon` values; map
// them to the semantic Icon keys for rendering. Unknown/custom defaults → 'type-custom'.
const REMINDER_ICON_KEY = {
  '🔑': 'type-new-closing',
  '🏡': 'type-sale-anniversary', '🎂': 'type-birthday', '🎉': 'type-new-year',
  '💝': 'type-valentines', '🎆': 'type-july4', '🦃': 'type-thanksgiving',
  '🎄': 'type-christmas', '📝': 'type-custom',
};
const iconKeyFor = (emoji) => REMINDER_ICON_KEY[emoji] || 'type-custom';


// Plain-English description of what date an anchored copy fires on.
function anchorNote(anchorId) {
  if (anchorId === 'happy-birthday')   return "the contact's birthday";
  if (anchorId === 'sale-anniversary') return 'the sale anniversary';
  if (anchorId === 'new-closing')      return '1–4 weeks after the closing (one time)';
  const h = BUILTIN_REMINDERS.find(r => r.id === anchorId);
  return h ? `${h.label}` : 'its anniversary';
}

/* ── Inline Assign Pane (renders as table rows) ── */
function AssignPane({ contact, draft, onUpdate, customTemplates, autoMap, dirty, saved, onSave, onClose, properties }) {
  // Sale Anniversary's sold date comes from the contact's linked property.
  const saleSoldDate = soldDateForContact(contact.id, properties);
  // The draft lives in the parent so the contact row can share the same
  // Save / Unsaved / Close controls. Nothing persists until "Save Assignment".
  const asgn = draft;
  const update = onUpdate;

  function emptyCustomDetail() {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const fiveYears = new Date(today);
    fiveYears.setFullYear(fiveYears.getFullYear() + 5);
    return { assigned: false, startDate: todayStr, endDate: fiveYears.toISOString().split('T')[0], interval: 'One Time' };
  }
  // A copy of a date-anchored system reminder schedules like its built-in: annual,
  // no end date. Only PLAIN customs get the One Time + 5-year defaults above.
  function baseDetailFor(ct) {
    if (ct.anchor) return { assigned: false, startDate: new Date().toISOString().split('T')[0], interval: 'Annual' };
    return { ...emptyCustomDetail(), interval: ct.interval || 'One Time' };   // default cadence from the reminder
  }

  // A manual toggle on this screen writes an EXPLICIT per-contact override that wins
  // over the agent's Auto-Enroll setting. "↺ Auto" clears it (back to following auto).
  function setExplicit(id, value, base) {
    const cur = typeof asgn[id] === 'object' ? asgn[id] : (base || {});
    const updates = { ...cur, assigned: value, explicit: true };
    delete updates.auto;   // a manual toggle is a deliberate choice, no longer "auto-enrolled"
    if (value) {
      updates.lastRunAt = '';
      updates.lastRunDate = '';
      updates.cycleId = Date.now();   // new assignment = new occurrence (lets a re-assigned reminder send again)
      if (!updates.startDate) updates.startDate = new Date().toISOString().split('T')[0];
    }
    update({ ...asgn, [id]: updates });
  }
  function resetAuto(id) {
    const next = { ...asgn };
    const cur = next[id];
    if (cur && typeof cur === 'object') {
      const { explicit, assigned, ...rest } = cur;   // eslint-disable-line no-unused-vars
      if (Object.keys(rest).length) next[id] = rest; else delete next[id];
    } else { delete next[id]; }
    update(next);
  }

  const totalAssigned = effectiveActiveCount(asgn, autoMap, contact, saleSoldDate, customTemplates);

  // Only reminders this scope OWNS: built-ins it inherited (has an entry for) and its
  // own custom reminders. Built-ins the level(s) above never published aren't owned, so
  // they must NOT be assignable here.
  // Inherited (read-only) templates are NOT assignable — the agent must Make a copy first.
  const ownedBuiltins = BUILTIN_REMINDERS.filter(r => (customTemplates || []).some(c => c.builtin && c.id === r.id && !c.inherited));
  const customList = (customTemplates || []).filter(c => !c.builtin && !c.base && !c.inherited);

  // One consistent status badge for BOTH built-in and custom reminders:
  //   • Excluded manually — a deliberate per-contact opt-out (+ ↺ Auto to undo)
  //   • Auto-Enrolled / Auto — reminder is auto-enrolled and this contact follows it
  //     (responsive: full label on desktop, short on mobile)
  //   • Assigned manually — a per-contact opt-in for a non-auto reminder (+ ↺ Auto)
  //   • Off — not auto-enrolled and not assigned
  function stateBadge(rid) {
    const detail = typeof asgn[rid] === 'object' ? asgn[rid] : {};
    const eff = effectiveAssigned(asgn[rid], autoMap, rid, contact, saleSoldDate);
    const auto = isAutoEnrolled(autoMap, rid);
    const resetBtn = <button className="assign-reset-link" title="Follow Auto-Enroll again" onClick={() => resetAuto(rid)}><Icon name="restore" size={16} /> Auto</button>;
    if (detail.explicit && !detail.assigned) return <><span className="assign-state-badge manual">Excluded manually</span>{resetBtn}</>;
    if (auto && eff) return <span className="assign-state-badge auto"><Icon name="repeat" size={14} /> <span className="lbl-full">Auto-Enrolled</span><span className="lbl-short">Auto</span></span>;
    if (eff) return <><span className="assign-state-badge manual">Assigned manually</span>{resetBtn}</>;
    return <span className="assign-state-badge off">Off</span>;
  }

  return (
    <tr className="ra-assign-pane-row">
      <td colSpan={3}>
        <div className="ra-assign-pane">
          {/* Built-in reminders (data-driven + holidays) */}
          <div className="assign-section">
            <div className="assign-section-label">Reminders</div>
            <p className="assign-section-hint">Auto-Enroll sends these automatically — toggle a contact here only to override.</p>
            {ownedBuiltins.map(r => {
              const eff = effectiveAssigned(asgn[r.id], autoMap, r.id, contact, saleSoldDate);
              const eligible = isEligible(r.id, contact, saleSoldDate);
              return (
                <div key={r.id} className={`assign-single-block ${eff ? 'assigned' : ''}`}>
                  <div className="assign-single-block-header">
                    <label className="assign-single-check">
                      <input type="checkbox" checked={eff} onChange={() => setExplicit(r.id, !eff)} />
                      <span className="assign-item-icon"><Icon name={iconKeyFor(r.icon)} size={20} /></span>
                      <span className="assign-item-label">{r.label}</span>
                    </label>
                    <span className="assign-tags">{stateBadge(r.id)}</span>
                  </div>
                  {eff && !eligible && r.id === 'happy-birthday' && (
                    <div className="assign-birthday-error">⚠ No birthday on file — set one for this reminder to send.</div>
                  )}
                  {eff && !eligible && r.id === 'sale-anniversary' && (
                    <div className="assign-soldate-hint">⚠ No sold property linked — link one on the Properties page to schedule the anniversary.</div>
                  )}
                  {eff && !eligible && r.id === 'new-closing' && (
                    <div className="assign-soldate-hint">⚠ No sold property linked — link one on the Properties page; sends once, 1–4 weeks after the closing.</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Custom templates */}
          {customList.length > 0 && (
            <div className="assign-section">
              <div className="assign-section-label">Custom Reminders</div>
              {customList.map(ct => {
                const detail = typeof asgn[ct.id] === 'object' ? asgn[ct.id] : {};
                const eff = effectiveAssigned(asgn[ct.id], autoMap, ct.id, contact, saleSoldDate);
                // Copies of built-ins are date-anchored (annual); plain customs are interval-driven.
                const anchorId = ct.anchor || null;
                const icon = anchorId ? (BUILTIN_REMINDERS.find(r => r.id === anchorId)?.icon || '📝') : '📝';
                const eligible = !anchorId || isEligible(anchorId, contact, saleSoldDate);
                return (
                  <div key={ct.id} className={`assign-single-block ${eff ? 'assigned' : ''}`}>
                    <div className="assign-single-block-header">
                      <label className="assign-single-check">
                        <input type="checkbox" checked={eff} onChange={() => setExplicit(ct.id, !eff, baseDetailFor(ct))} />
                        <span className="assign-item-icon"><Icon name={iconKeyFor(icon)} size={20} /></span>
                        <span className="assign-item-label">{ct.label}</span>
                      </label>
                      <span className="assign-tags">{stateBadge(ct.id)}</span>
                    </div>
                    {/* Anchored copy of Happy Birthday / Sale Anniversary needs the contact's data. */}
                    {eff && anchorId === 'happy-birthday' && !eligible && (
                      <div className="assign-birthday-error">⚠ No birthday on file — set one for this reminder to send.</div>
                    )}
                    {eff && anchorId === 'sale-anniversary' && !eligible && (
                      <div className="assign-soldate-hint">⚠ No sold property linked — link one on the Properties page to schedule the anniversary.</div>
                    )}
                    {eff && anchorId === 'new-closing' && !eligible && (
                      <div className="assign-soldate-hint">⚠ No sold property linked — link one on the Properties page; sends once, 1–4 weeks after the closing.</div>
                    )}
                    {/* Anchored copies fire on the built-in's schedule. */}
                    {eff && anchorId && (
                      <div className="assign-anchor-note">🔁 {anchorId === 'new-closing' ? 'One time' : 'Annual'} — fires automatically on {anchorNote(anchorId)}.</div>
                    )}
                    {/* Plain customs repeat on the cadence set ON the reminder (read-only here). */}
                    {eff && !anchorId && (() => {
                      const iv = detail.interval || ct.interval || 'One Time';
                      return <div className="assign-anchor-note">🔁 {INTERVAL_LABELS[iv] || iv} — set on the reminder.</div>;
                    })()}
                  </div>
                );
              })}
            </div>
          )}

          <div className="assign-pane-footer">
            <span className="assign-count">{totalAssigned} active reminder{totalAssigned !== 1 ? 's' : ''}</span>
            <div className="assign-pane-footer-actions">
              {dirty
                ? <span className="assign-unsaved-hint">● Unsaved changes</span>
                : (saved && <span className="rt-saved-indicator">✓ Saved</span>)}
              <button className="contacts-import-cancel-btn" onClick={onClose}>Close</button>
              <button className="rt-save-btn" onClick={onSave} disabled={!dirty}>Save Assignment</button>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

/* ── Main Component ── */
function ReminderAssignments({ scope }) {
  const K_ASSIGNMENTS = scopedKey('ira_assignments', scope);
  const K_CONTACTS    = scopedKey('ira_contacts', scope);
  const K_CUSTOM      = scopedKey('ira_custom_templates', scope);
  const K_PROPERTIES  = scopedKey('ira_properties', scope);
  const K_AUTOENROLL  = scopedKey('ira_autoenroll', scope);
  const [autoMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_AUTOENROLL) || '{}'); } catch { return {}; }
  });
  const [contacts, setContacts]             = useState([]);
  const [assignments, setAssignments]       = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_ASSIGNMENTS) || '{}'); } catch { return {}; }
  });
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft]   = useState(null);
  const [dirty, setDirty]   = useState(false);
  const [saved, setSaved]   = useState(false);
  const [search, setSearch] = useState('');
  const [customTemplates, setCustomTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_CUSTOM) || '[]'); } catch { return []; }
  });
  const [properties, setProperties] = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_PROPERTIES) || '[]'); } catch { return []; }
  });

  // Pure reset — no guard. Used after Save and by the guarded exit paths below.
  function resetDraft() {
    setOpenId(null);
    setDraft(null);
    setDirty(false);
    setSaved(false);
  }
  async function openContact(c) {
    // Switching rows or toggling shut abandons the current draft — confirm first.
    if (!(await confirmDiscard(dirty))) return;
    if (openId === c.id) { resetDraft(); return; }
    setOpenId(c.id);
    setDraft(migrateAssignment(assignments[c.id]));
    setDirty(false);
    setSaved(false);
  }
  async function closeContact() {
    if (!(await confirmDiscard(dirty))) return;
    resetDraft();
  }
  function updateDraft(next) {
    setDraft(next);
    setDirty(true);
    setSaved(false);
  }
  function saveDraft() {
    setAssignments(prev => ({ ...prev, [openId]: draft }));
    setDirty(false);
    setSaved(true);
  }

  const q = search.toLowerCase().trim();
  // Filter by contact name OR an assigned reminder's name. The reminder-name
  // match surfaces every contact that actually has that reminder assigned.
  const allReminderDefs = [
    ...BUILTIN_REMINDERS.filter(r => customTemplates.some(c => c.builtin && c.id === r.id && !c.inherited)).map(r => ({ id: r.id, label: r.label })),
    ...customTemplates.filter(ct => !ct.builtin && !ct.base && !ct.inherited).map(ct => ({ id: ct.id, label: ct.label })),
  ];
  function contactMatches(c) {
    if (`${c.firstName} ${c.lastName}`.toLowerCase().includes(q)) return true;
    const asgn = migrateAssignment(assignments[c.id]);
    const soldDate = soldDateForContact(c.id, properties);
    return allReminderDefs.some(r =>
      r.label.toLowerCase().includes(q) &&
      effectiveAssigned(asgn[r.id], autoMap, r.id, c, soldDate));
  }
  const displayed = q ? contacts.filter(contactMatches) : contacts;

  useEffect(() => {
    try { setContacts(JSON.parse(localStorage.getItem(K_CONTACTS) || '[]')); } catch { setContacts([]); }
    try { setCustomTemplates(JSON.parse(localStorage.getItem(K_CUSTOM) || '[]')); } catch { setCustomTemplates([]); }
    try { setProperties(JSON.parse(localStorage.getItem(K_PROPERTIES) || '[]')); } catch { setProperties([]); }
    // Strip adHoc arrays from existing assignments
    try {
      const raw = JSON.parse(localStorage.getItem(K_ASSIGNMENTS) || '{}');
      let changed = false;
      for (const id of Object.keys(raw)) {
        if (raw[id]?.adHoc) { delete raw[id].adHoc; changed = true; }
      }
      if (changed) { localStorage.setItem(K_ASSIGNMENTS, JSON.stringify(raw)); setAssignments(raw); }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(K_ASSIGNMENTS, JSON.stringify(assignments));
  }, [assignments, K_ASSIGNMENTS]);

  // Back-fill missing start/end dates for all existing assigned single reminders
  useEffect(() => {
    const todayStr = new Date().toISOString().split('T')[0];

    setAssignments(prev => {
      let changed = false;
      const updated = { ...prev };
      for (const contactId of Object.keys(updated)) {
        const asgn = migrateAssignment(updated[contactId]);
        let asgnChanged = false;
        for (const r of BUILTIN_REMINDERS) {
          const detail = asgn[r.id];
          if (detail?.assigned && !detail.startDate) {
            asgn[r.id] = { ...detail, startDate: todayStr };
            asgnChanged = true;
          }
        }
        if (asgnChanged) { updated[contactId] = asgn; changed = true; }
      }
      return changed ? updated : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Simulated execution: when the page loads, any assigned annual reminder whose
  // anniversary has been reached records that anniversary as its last run.
  // Sale Anniversary → sold-date anniversary.
  useEffect(() => {
    const today = new Date();
    let props = [];
    try { props = JSON.parse(localStorage.getItem(K_PROPERTIES) || '[]'); } catch {}
    setAssignments(prev => {
      let changed = false;
      const updated = { ...prev };
      for (const contactId of Object.keys(updated)) {
        const asgn = migrateAssignment(updated[contactId]);
        let asgnChanged = false;
        const soldDate = soldDateForContact(contactId, props);
        const saleRun = computeSaleAnnivRun({ ...asgn['sale-anniversary'], soldDate }, today);
        if (saleRun) { asgn['sale-anniversary'] = { ...asgn['sale-anniversary'], lastRunDate: saleRun }; asgnChanged = true; }
        if (asgnChanged) { updated[contactId] = asgn; changed = true; }
      }
      return changed ? updated : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Quick bulk-assign drives the SAME assignments state, so the per-contact table
  // updates live and persists through the normal save effect.
  function handleBulkApply(reminderIds, contactIds) {
    const { updated, birthdaySkips } = computeBulkAssign(assignments, reminderIds, contactIds, contacts, customTemplates);
    setAssignments(updated);
    return { birthdaySkips };
  }
  function handleBulkUnapply(reminderIds, contactIds) {
    const { updated } = computeBulkUnassign(assignments, reminderIds, contactIds, autoMap);
    setAssignments(updated);
  }

  return (
    <div className="ra-container">
      <div className="ra-header">
        <h2>Assign Reminders</h2>
        <p>Bulk-assign reminders to many contacts at once, then fine-tune any contact below.</p>
      </div>

      {contacts.length === 0 ? (
        <div className="ra-empty">
          <span><Icon name="contacts" size={20} /></span>
          <p>No contacts yet. Import or add contacts first.</p>
        </div>
      ) : (
        <>
        <BulkAssignPanel contacts={contacts} customTemplates={customTemplates} onApply={handleBulkApply} onUnassign={handleBulkUnapply} />
        <div className="ra-percontact-head">Assigned Reminders</div>
        <div className="ra-toolbar">
          <input
            className="ra-search"
            type="text"
            placeholder="🔍 Filter by contact or reminder…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {q && (
            <span className="ra-search-count">
              {displayed.length} of {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="ra-table-wrap">
          <table className="ra-table">
            <thead>
              <tr>
                <th className="ra-th-icon-col"></th>
                <th>Contact</th>
                <th className="ra-th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={3} className="ra-no-results">No contacts match “{search}”.</td>
                </tr>
              ) : displayed.map(c => {
                const count = effectiveActiveCount(assignments[c.id], autoMap, c, soldDateForContact(c.id, properties), customTemplates);
                const isOpen = openId === c.id;
                return (
                  <React.Fragment key={c.id}>
                    <tr className={`ra-contact-row ${isOpen ? 'ra-row-open' : ''}`}>
                      <td className="ra-icon-col">
                        <button
                          className={`icon-btn assign-icon-btn ${isOpen ? 'active' : ''}`}
                          title="Manage reminders"
                          onClick={() => openContact(c)}
                        ><Icon name="reminder-assignments" size={16} /></button>
                        {count > 0 && <span className="ra-icon-badge">{count}</span>}
                      </td>
                      <td className="ra-contact-cell">
                        <span className="ra-contact-name">{c.firstName} {c.lastName}</span>
                      </td>
                      <td className="ra-row-actions-col">
                        {isOpen && (
                          <div className="ra-row-actions">
                            {dirty
                              ? <span className="assign-unsaved-hint">● Unsaved changes</span>
                              : (saved && <span className="rt-saved-indicator">✓ Saved</span>)}
                            <button className="contacts-import-cancel-btn" onClick={closeContact}>Close</button>
                            <button className="rt-save-btn" onClick={saveDraft} disabled={!dirty}>Save Assignment</button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {isOpen && draft && (
                      <AssignPane
                        contact={c}
                        draft={draft}
                        onUpdate={updateDraft}
                        customTemplates={customTemplates}
                        autoMap={autoMap}
                        properties={properties}
                        dirty={dirty}
                        saved={saved}
                        onSave={saveDraft}
                        onClose={closeContact}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

export default ReminderAssignments;
